import * as React from 'react';
import { MoreVertical } from 'lucide-react';
import { cn } from '@repo/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './DropdownMenu';
import { ConfirmDialog } from './ConfirmDialog';
import { Button } from './Button';

export interface EntityAction {
  label: string;
  onClick: () => void | Promise<void>;
  icon?: React.ReactNode;
  variant?: 'default' | 'destructive';
  disabled?: boolean;
  hidden?: boolean;
  requiresConfirmation?: boolean;
  confirmTitle?: string;
  confirmDescription?: string;
  confirmLabel?: string;
}

export interface EntityActionMenuProps {
  actions: EntityAction[];
  triggerLabel?: string; // If omitted, renders MoreVertical icon button
  className?: string;
}

export function EntityActionMenu({
  actions,
  triggerLabel,
  className,
}: EntityActionMenuProps) {
  const [pendingAction, setPendingAction] = React.useState<EntityAction | null>(null);
  const [confirmLoading, setConfirmLoading] = React.useState(false);

  const visibleActions = actions.filter((action) => !action.hidden);
  if (visibleActions.length === 0) {
    return null;
  }

  const handleActionClick = (action: EntityAction) => {
    if (action.disabled) return;
    if (action.variant === 'destructive' || action.requiresConfirmation) {
      setPendingAction(action);
    } else {
      void action.onClick();
    }
  };

  const handleConfirm = async () => {
    if (!pendingAction) return;
    try {
      setConfirmLoading(true);
      await pendingAction.onClick();
      setPendingAction(null);
    } catch (err) {
      console.error('Failed to execute entity action:', err);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleCancel = () => {
    if (!confirmLoading) {
      setPendingAction(null);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {triggerLabel ? (
            <Button variant="secondary" size="sm" className={className}>
              {triggerLabel}
            </Button>
          ) : (
            <button
              type="button"
              aria-label="More actions"
              className={cn(
                'inline-flex h-8 w-8 items-center justify-center rounded-sm border border-border bg-surface-1 text-text-secondary transition-colors hover:bg-surface-2 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500',
                className,
              )}
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {visibleActions.map((action, idx) => (
            <DropdownMenuItem
              key={`${action.label}-${idx}`}
              variant={action.variant}
              disabled={action.disabled}
              onSelect={() => handleActionClick(action)}
            >
              {action.icon && (
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center [&>svg]:h-3.5 [&>svg]:w-3.5',
                    action.variant === 'destructive'
                      ? 'text-danger-text'
                      : 'text-text-secondary',
                  )}
                >
                  {action.icon}
                </span>
              )}
              <span>{action.label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={pendingAction !== null}
        title={
          pendingAction?.confirmTitle ??
          (pendingAction?.variant === 'destructive'
            ? `Confirm ${pendingAction?.label}`
            : 'Confirm Action')
        }
        description={
          pendingAction?.confirmDescription ??
          `Are you sure you want to proceed with "${pendingAction?.label}"? This action cannot be undone.`
        }
        confirmLabel={
          pendingAction?.confirmLabel ??
          (pendingAction?.variant === 'destructive' ? 'Delete' : 'Confirm')
        }
        loading={confirmLoading}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
}
